<script setup>
import {useStore} from "vuex";
import {computed, onUnmounted, ref, watch} from "vue";
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const store = useStore()
const connectionError = computed(()=> store.getters['WebSocketSession/connectionError'])
const activity = computed(()=> store.getters['Activities/activity'])
const sessionInfo = computed(()=> store.getters['WebSocketSession/sessionInfo'])
const sessionError = computed(()=> store.getters['WebSocketSession/sessionError'])
const timeRemaining = computed(()=> store.getters['WebSocketSession/timeRemaining'])
const results = computed(()=> store.getters['WebSocketSession/results'])

const reconnectInterval = ref(null)
const countdownInterval = ref(null)
const remainingSeconds = ref(null)
const search = ref('')
const loading = ref(false)
const buttonText = computed(()=>{
  if(sessionInfo.value.status === 'WAITING_FOR_PARTICIPANTS'){
    return t('gamification.start_test')
  } else if(sessionInfo.value.status === 'IN_PROGRESS' ){
    return t('gamification.stop_test')
  } else if( sessionInfo.value.status === 'COMPLETED') {
    return t('gamification.completed')
  } else if(sessionInfo.value.status === 'CANCELLED') {
    return t('gamification.cancelled')
  } else {
    return ''
  }
})

const buttonDisabled = computed(()=> sessionInfo.value.status === 'COMPLETED' || sessionInfo.value.status === 'CANCELLED')

const testStatusErrorText = ref('')

const testRunning = computed(()=> sessionInfo.value.status === 'IN_PROGRESS')
const formattedTimeRemaining = computed(()=> {
  if(remainingSeconds.value === null) return ''
  const minutes = Math.floor(remainingSeconds.value / 60)
  const seconds = String(remainingSeconds.value % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
})

watch(timeRemaining, (time) => {
  const minutes = Number(time.remainingMinutes)
  const seconds = Number(time.remainingSeconds)
  if(Number.isNaN(minutes) || Number.isNaN(seconds)) return
  remainingSeconds.value = minutes * 60 + seconds
}, {deep: true, immediate: true})

watch(testRunning, (running) => {
  if(running){
    startCountdown()
  } else {
    stopCountdown()
  }
}, {immediate: true})

watch(()=> activity.value.sessionCode, (sessionCode) => {
  if(sessionCode) loadResults()
}, {immediate: true})

watch(connectionError, (error) => {
  reconnectHandler(error)
})
watch(sessionError, async (error) => {
  console.log('session error ', error)
  if(error === 'INVALID_TOKEN'){
    await store.dispatch('WebSocketSession/getAccessToken', activity.value.sessionCode)
    await store.dispatch('WebSocketSession/clearSessionError')
  }
})

async function reconnectHandler(connError){
  if (connError === true) {
    if (reconnectInterval.value) return
    console.info('Reconnecting websocket every 3 seconds...')
    testStatusErrorText.value = t('gamification.connection_error')
    reconnectInterval.value = setInterval(async () => {
      await store.dispatch('WebSocketSession/getAccessToken', activity.value.sessionCode)
      console.log('Attempting reconnect...')
      await store.dispatch('WebSocketSession/setSocketConfig')
      await store.dispatch('WebSocketSession/connect')
      await getSessionInfo()
    }, 3000)

  } else {
    if (reconnectInterval.value) {
      clearInterval(reconnectInterval.value)
      reconnectInterval.value = null
      testStatusErrorText.value = ""
    }
  }
}

function startCountdown(){
  if(countdownInterval.value) return
  countdownInterval.value = setInterval(()=>{
    if(remainingSeconds.value > 0){
      remainingSeconds.value--
    }
  }, 1000)
}

function stopCountdown(){
  if(countdownInterval.value){
    clearInterval(countdownInterval.value)
    countdownInterval.value = null
  }
}

function participantStatus(status){
  if(status === 'JOINED'){
    return {text: t('gamification.connected'), color: 'bg-primary'}
  } else if(status === 'IN_PROGRESS'){
    return {text: t('gamification.in_progress'), color: 'bg-light-blue-darken-3'}
  } else if(status === 'COMPLETED'){
    return {text: t('gamification.submitted'), color: 'bg-primary'}
  } else if(status === 'DISCONNECTED'){
    return {text: t('gamification.disconnected'), color: 'bg-warning'}
  } else if(status === 'REJOINED'){
    return {text: t('gamification.connected'), color: 'bg-primary'}
  } else {
    return {text: t('gamification.no_info'), color: 'bg-black'}
  }
}

async function toggleTestExecution(){
  if(sessionInfo.value.status === 'WAITING_FOR_PARTICIPANTS'){
    await startTest()
  } else if(sessionInfo.value.status === 'IN_PROGRESS'){
    await stopTest()
  }
}

/**
 * The start request goes out over the WebSocket, so the session is not running the moment the call
 * returns. Wait for the admin channel to report IN_PROGRESS instead of asking the REST API straight
 * away - that race is what used to report a failure on every first attempt.
 */
function waitForSessionStart(timeoutMs = 8000){
  return new Promise((resolve) => {
    if(sessionInfo.value.status === 'IN_PROGRESS'){
      resolve(true)
      return
    }
    let unwatch = null
    const timer = setTimeout(() => {
      if(unwatch) unwatch()
      resolve(false)
    }, timeoutMs)
    unwatch = watch(() => sessionInfo.value.status, (status) => {
      if(status === 'IN_PROGRESS'){
        clearTimeout(timer)
        unwatch()
        resolve(true)
      }
    })
  })
}

async function startTest(){
  loading.value = true
  store.dispatch('WebSocketSession/startSession')
  const started = await waitForSessionStart()
  if(!started){
    testStatusErrorText.value = t('gamification.general_error')
  } else {
    testStatusErrorText.value = ""
    const response = await getSessionInfo()
    await store.dispatch('WebSocketSession/setInitialTimeRemaining', {
      duration: response.duration,
      sessionCode: response.sessionCode
    })
    await store.dispatch('Activities/testExecuted')
  }
  loading.value = false
}

async function stopTest(){
  loading.value = true
  store.dispatch('WebSocketSession/endSession')
  store.dispatch('WebSocketSession/setHeartBeatInterval', null)
  const response = await getSessionInfo()
  console.log('response ', response)
  loading.value = false
}

async function getSessionInfo(){
  return await store.dispatch(
      'WebSocketSession/getTestSessionDetails',
      activity.value.sessionCode
  )
}

const participantsHeaders = [
  { key: 'username', title: 'Login', width: "25%" },
  { key: 'fullName', title: t('gamification.full_name'), width: "40%" },
  { key: 'status', title: t('gamification.status'), width: "35%" },
]

const resultsHeaders = [
  { key: 'rank', title: t('gamification.rank'), width: "10%", sortable: false },
  { key: 'login', title: 'Login', width: "20%" },
  { key: 'fullName', title: t('gamification.full_name'), width: "30%" },
  { key: 'points', title: t('gamification.points'), width: "15%" },
  { key: 'successRate', title: t('gamification.success_rate'), width: "15%" },
  { key: 'submittedAt', title: t('gamification.submitted_at'), width: "10%" },
]

const resultRows = computed(()=> results.value.map((result, index) => ({
  rank: result.submitted ? index + 1 : null,
  login: result.login,
  fullName: result.fullName ?? '',
  points: result.submitted ? `${result.score}/${result.maxPoints}` : t('gamification.not_submitted'),
  successRate: result.submitted && result.maxPoints > 0 ? Math.round((result.score / result.maxPoints) * 100) : null,
  submittedAt: formatTime(result.date)
})))

function formatTime(timestamp){
  if(!timestamp) return ''
  return new Date(timestamp).toLocaleTimeString('cs-CZ', {hour: '2-digit', minute: '2-digit'})
}

async function loadResults(){
  if(activity.value.sessionCode){
    await store.dispatch('WebSocketSession/fetchResults', activity.value.sessionCode)
  }
}

onUnmounted(()=>{
  stopCountdown()
  if(reconnectInterval.value){
    clearInterval(reconnectInterval.value)
    reconnectInterval.value = null
  }
  store.dispatch('WebSocketSession/disconnect')
  store.dispatch('WebSocketSession/setHeartBeatInterval', null)
  store.dispatch('WebSocketSession/clear')
})

</script>

<template>
  <div class="mb-16">
    <h3 class="pt-5"> {{ $t('gamification.user_status') }} </h3>
    <!-- always in the layout so that showing an error does not push the rest of the page down -->
    <div class="text-error pt-2 status-error-line">{{ testStatusErrorText }}</div>
    <div class="d-flex justify-space-between">
      <div class="w-50">
        <v-text-field v-model="search" clearable :placeholder="$t('gamification.search_user')" color="primary" variant="underlined" prepend-icon="search" hide-details />
      </div>
      <div class="d-flex align-center ga-4 pt-5">
        <div v-if="testRunning" class="d-flex align-center ga-1">
          <v-icon icon="timer" color="primary" />
          <span class="text-subtitle-1">{{ $t('gamification.time_remaining') }}</span>
          <span class="text-h6 font-weight-medium">{{ formattedTimeRemaining }}</span>
        </div>
        <v-btn @click="toggleTestExecution" :disabled="buttonDisabled" :loading="loading" color="primary" rounded variant="flat"> {{ buttonText }} </v-btn>
      </div>
    </div>
    <v-data-table
        :items="sessionInfo.participants"
        :headers="participantsHeaders"
        :search="search"
        :no-data-text="$t('gamification.no_users_available')"
    >
      <template v-slot:item.status="{ value }">
        <div class="d-flex align-center ga-2">
          <div :class="`status-circle rounded ${participantStatus(value).color}`"></div>
          {{ participantStatus(value).text }}
        </div>

      </template>
    </v-data-table>
    <div v-if="resultRows.length > 0" class="pt-8">
      <h3> {{$t('gamification.test_results') }}</h3>
      <v-data-table
          :items="resultRows"
          :headers="resultsHeaders"
          :no-data-text="$t('gamification.no_data')"
      >
        <template v-slot:item.rank="{ value }">
          {{ value === null ? '–' : `${value}.` }}
        </template>
        <template v-slot:item.successRate="{ value }">
          {{ value === null ? '–' : `${value} %` }}
        </template>
      </v-data-table>
    </div>
  </div>

</template>

<style scoped>
.status-circle {
  width: 0.5em;
  height: 0.5em;
}

.status-error-line {
  min-height: 1.5rem;
}
</style>